'use client';

import { ArrowRight, Check, Hand, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

const PROGRESS_GREEN = 'oklch(0.62 0.13 148)';
const SUCCESS_GREEN = 'oklch(0.52 0.15 148)';
const STEP_BADGE_BG = 'oklch(0.92 0.08 148)';
const STEP_BADGE_FG = 'oklch(0.32 0.12 148)';

interface FixturePendingProps {
  teamsRegistered: number;
  minRequired: number;
  maxAllowed: number;
  signupDeadlineDate: string;
  onCreateFixture: () => void;
  /** Header label; a knockout-only tournament has no group stage to announce. */
  title?: string;
}

const formatDeadline = (dateString: string) =>
  new Date(dateString).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function FixturePending({
  teamsRegistered,
  minRequired,
  maxAllowed,
  signupDeadlineDate,
  onCreateFixture,
  title = 'Fase de grupos pendiente',
}: FixturePendingProps) {
  const safeMax = Math.max(maxAllowed, 1);
  const minReached = teamsRegistered >= minRequired;
  const teamsRemaining = Math.max(minRequired - teamsRegistered, 0);
  const isSignupClosed =
    new Date(signupDeadlineDate).getTime() <= Date.now();
  const canCreate = isSignupClosed && minReached;

  const fillPct = Math.min(100, (teamsRegistered / safeMax) * 100);
  const minMarkerPct = Math.min(100, (minRequired / safeMax) * 100);

  const benefits = [
    'Modo borrador privado',
    'Editás antes de publicar',
    'Cierra inscripciones al crear',
    'Activás cuando estés listo',
  ];

  // Button copy follows restriction hierarchy: deadline first, then min teams.
  const buttonContent = !isSignupClosed
    ? {
        icon: <Lock className="h-4 w-4" strokeWidth={2.2} />,
        text: 'Esperando cierre de inscripciones',
      }
    : !minReached
      ? {
          icon: <Lock className="h-4 w-4" strokeWidth={2.2} />,
          text: `${teamsRemaining === 1 ? 'Falta' : 'Faltan'} ${teamsRemaining} ${teamsRemaining === 1 ? 'equipo' : 'equipos'} para crear el fixture`,
        }
      : {
          icon: <ArrowRight className="h-4 w-4" strokeWidth={2.2} />,
          text: 'Crear fixture manual',
        };

  return (
    <div className="flex flex-col gap-5">
      {/* 1. Header */}
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: PROGRESS_GREEN }}
        />
        <span
          className="text-[11px] font-bold uppercase tracking-[0.06em]"
          style={{ color: PROGRESS_GREEN }}
        >
          {title}
        </span>
        <span className="text-[12px] text-slate-500">
          · El torneo aún no tiene fixture creado
        </span>
      </header>

      {/* 2. Restriction hierarchy: condition 1 = deadline, condition 2 = min teams */}
      <section className="flex flex-col gap-3">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.04em] text-slate-500">
          Para crear el fixture
        </h3>

        {/* Condition 1: signup deadline */}
        <ConditionRow
          step={1}
          met={isSignupClosed}
          title="Cierre del período de inscripciones"
          description={
            isSignupClosed
              ? `Las inscripciones cerraron el ${formatDeadline(signupDeadlineDate)}.`
              : `Las inscripciones cierran el ${formatDeadline(signupDeadlineDate)}.`
          }
        />

        {/* Condition 2: minimum teams (with progress visualization) */}
        <ConditionRow
          step={2}
          met={minReached}
          dimmed={!isSignupClosed}
          title="Mínimo de equipos inscriptos alcanzado"
        >
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-bold text-slate-800">
                Equipos inscriptos
              </span>
              <span className="text-[12px] font-medium">
                <span
                  className="font-bold"
                  style={{ color: minReached ? SUCCESS_GREEN : PROGRESS_GREEN }}
                >
                  {teamsRegistered}
                </span>
                <span className="text-slate-400"> / {maxAllowed} máx.</span>
              </span>
            </div>

            <div className="relative mt-3">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full transition-[width,background-color] duration-300"
                  style={{
                    width: `${fillPct}%`,
                    backgroundColor: minReached ? SUCCESS_GREEN : PROGRESS_GREEN,
                  }}
                />
              </div>

              <div
                className="pointer-events-none absolute top-0 -translate-x-1/2"
                style={{ left: `${minMarkerPct}%` }}
              >
                <div className="h-2 w-[2px] bg-slate-700" />
                <div className="absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap rounded-full border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                  Mín {minRequired}
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">0</span>
              <span
                className="font-semibold"
                style={{ color: minReached ? SUCCESS_GREEN : PROGRESS_GREEN }}
              >
                {minReached
                  ? 'Mínimo alcanzado ✓'
                  : `${teamsRemaining === 1 ? 'Falta' : 'Faltan'} ${teamsRemaining} para completar el mínimo`}
              </span>
              <span className="text-slate-400">{maxAllowed}</span>
            </div>
          </div>
        </ConditionRow>
      </section>

      {/* 3. Manual creation card */}
      <section className="overflow-hidden rounded-[11px] border-2 border-primary">
        <div className="flex items-center gap-3 bg-primary px-4 py-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/15">
            <Hand className="h-4 w-4 text-white" strokeWidth={2.2} />
          </div>
          <h3 className="flex-1 text-[13px] font-bold text-white">
            Crear fixture manualmente
          </h3>
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            Borrador
          </span>
        </div>

        <div className="space-y-4 px-4 py-3.5">
          <p className="text-[12.5px] leading-[1.55] text-slate-700">
            Una vez cerrado el período de inscripciones, podés generar el
            fixture vos mismo. Se crea en modo borrador: solo vos lo ves como
            manager, hasta que decidas activarlo para los jugadores.
          </p>

          <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
            {benefits.map((benefit) => (
              <li
                key={benefit}
                className="flex items-center gap-1.5 text-[11.5px] text-slate-700"
              >
                <Check
                  className="h-3.5 w-3.5 shrink-0"
                  strokeWidth={2.2}
                  style={{ color: SUCCESS_GREEN }}
                />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={onCreateFixture}
            disabled={!canCreate}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity',
              !canCreate && 'cursor-not-allowed opacity-45',
            )}
          >
            {canCreate ? (
              <>
                <span>{buttonContent.text}</span>
                {buttonContent.icon}
              </>
            ) : (
              <>
                {buttonContent.icon}
                <span>{buttonContent.text}</span>
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

interface ConditionRowProps {
  step: number;
  met: boolean;
  dimmed?: boolean;
  title: string;
  description?: string;
  children?: React.ReactNode;
}

function ConditionRow({
  step,
  met,
  dimmed = false,
  title,
  description,
  children,
}: ConditionRowProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-[10px] border bg-white px-4 py-3 transition-opacity',
        met
          ? 'border-slate-200'
          : 'border-slate-200',
        dimmed && 'opacity-60',
      )}
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
        style={
          met
            ? { backgroundColor: SUCCESS_GREEN, color: 'white' }
            : {
                backgroundColor: STEP_BADGE_BG,
                color: STEP_BADGE_FG,
              }
        }
      >
        {met ? <Check className="h-3.5 w-3.5" strokeWidth={2.6} /> : step}
      </div>
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-slate-900">{title}</p>
        {description && (
          <p className="mt-0.5 text-[12px] text-slate-500">{description}</p>
        )}
        {children}
      </div>
    </div>
  );
}
